import { memo, useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type FormEvent, type KeyboardEvent, type ReactNode, type RefObject } from 'react'
import { ArrowDown, ArrowUp, Ban, CircleAlert, Ellipsis, LoaderCircle, LogIn, MessageCircle, RotateCw, Timer, Trash2, UserPlus } from 'lucide-react'
import { Avatar, Badge, Button, ConfirmDialog, IconButton, Menu, MenuItem, MenuSeparator, Notice, TextField, toast } from '@/components/play'
import { apiFailure } from '@/lib/apiFailure'
import { personToneKey } from '@/lib/profile'
import { CHAT_MAX_MESSAGES, CHAT_MESSAGE_MAX_LENGTH, chatBlock, initialsSource, isChatCard, nameOrHandle, profileAvatarUrl, visibleName, visibleText } from '@/lib/watch/rules'
import { useStagePulse } from '@/lib/watch/stagePulse'
import type { ChatBlock, ChatMessage } from '@/lib/watch/types'
import type { StageChat, StageChatBan } from '@/lib/watch/useStageChat'
import { cn } from '@/lib/utils'
import { ChatGifImage, ChatTime, EmojiPicker, GifPicker, LikeButton, TypingLine } from './ChatExtras'
import { StackedGlyph, WaitingState } from './StateGlyph'
import { StageCard, type StageCardAnswer } from './StageCard'
import './watch.css'

export interface ChatPanelProps {
  chat: StageChat
  signedIn: boolean
  /** This viewer hosts the stage: each message gets the moderation menu. */
  isHost: boolean
  /** The stage's hosts. Their messages carry a Host badge and can only be deleted, never timed out or removed. */
  hostUserIds: string[]
  /** This viewer: their own messages scroll the log down and can only be deleted. */
  currentUserId?: string | null
  onSignIn: () => void
  /** The composer took focus: the viewer is about to type (the phone takes it as picking the Chat tab). */
  onComposerFocus?: () => void
  /** Above the log, inside the region and under its h2: the page's `ChatHead` (the stage's host) and `Audience`. */
  head?: ReactNode
  /**
   * A host who may invite on this stage (`viewer.can_invite`): a viewer's message menu leads with Invite on stage, which
   * calls this with the author. Its promise shows the invite in progress on the item; it answers with its own toast.
   */
  onInviteOnStage?: (user: ChatMessage['user']) => Promise<unknown> | void
  /**
   * Everyone with a seat on a stage of the event now (hosts and guests, live): they aren't offered Invite on stage (the
   * server would answer `already_on_stage`).
   */
  seatedUserIds?: readonly string[]
  /**
   * The viewer's own invite on this stage, as the page knows it: the card that answers for it (their latest invite card
   * here) carries Join stage and Not now while it is open, then how it stands. Their other invite cards read as
   * answered. Never on someone else's card.
   */
  invite?: ChatInvite | null
  /** Pinned under the log, above the composer: the page's invite when no card in the log carries it. */
  prompt?: ReactNode
  /** The API can send GIFs (`StagePayload.chat.gifs`): the composer offers its GIF button. */
  gifs?: boolean
  className?: string
}

/** The viewer's open invite on the stage, for the card that carries it (`ChatPanelProps.invite`). */
export interface ChatInvite {
  cardId: string
  answered: StageCardAnswer
  onJoin(): void
  /** Not now; absent when it would change nothing (`note` says why). */
  onDecline?(): Promise<unknown>
  /** Beside Join stage instead of Not now (`StageCard`'s `note`). */
  note?: string
}

/**
 * The viewer's reduced-motion setting now. Read when scrolling rather than at render: `motion/react`'s
 * `useReducedMotion` takes it once and doesn't follow a change made mid-session.
 */
function prefersReducedMotion() {
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/** How close to the end of the log still counts as at the bottom, in pixels. */
const BOTTOM_SLACK_PX = 32

/** What the server says when a host tries to time out or remove a host (a 422; the client can't see off-stage hosts). */
const HOSTS_CANT_BE_MODERATED = 'Hosts can’t be timed out or removed'

const NO_KEYS: string[] = []

type Moderation = 'none' | 'delete' | 'full'

interface RowActions {
  onDelete: (message: ChatMessage) => void
  onTimeout: (message: ChatMessage) => void
  /** Asks first. `returnTo` is the row's menu button, where focus goes back if the removal is cancelled. */
  onRemove: (message: ChatMessage, returnTo: HTMLElement | null) => void
  onRetry: (message: ChatMessage) => void
  /** Invite on stage: the page's, with its toast. */
  onInvite?: (message: ChatMessage) => Promise<unknown> | void
  /** Likes or unlikes a message; signed out, asks to sign in first. Absent without the stage's pulse. */
  onLike?: (message: ChatMessage) => void
  /** The message log, which takes focus when a moderation action takes the focused row away. */
  log: RefObject<HTMLDivElement>
}

const rowKey = (message: ChatMessage) => message.localId ?? message.id

/**
 * The rows a ban hides: the person's messages (their pending and failed ones stay shown; counting those too is
 * harmless). Their cards stay, as on the server.
 */
const rowsBy = (messages: ChatMessage[], userId: string) => messages.filter((message) => message.user.id === userId && !isChatCard(message))

/** A card in the log: memoised, so typing in the composer passes it by as it does the messages. */
const CardRow = memo(StageCard)

/**
 * The author's first name when anything in it draws, else their handle; "Former viewer" for someone who has since
 * cleared both. A first name of only invisible characters (U+3164, a zero-width space, a Braille blank) counts as none:
 * it would show a blank name, and the handle is off screen.
 */
function authorName(user: ChatMessage['user']) {
  return nameOrHandle(user.name, user.handle, 'Former viewer')
}

/**
 * The author's name for a sentence (a label, a toast, a dialog title), without what draws nothing: a first name keeps
 * its direction controls (the server cleans only bodies), and an override would reorder the rest of the sentence.
 */
function plainName(user: ChatMessage['user']) {
  return visibleText(authorName(user)) || 'Former viewer'
}

/** A 422 from the ban endpoint: the target is a host there (an off-stage team member, say). */
const refusedAsHost = (error: unknown) => apiFailure(error).status === 422

/**
 * The stage's chat (docs/features/prototypes/watch-experience): an optional head (the page's stage owner row), the
 * message log, a "New messages" pill while scrolled up, and the composer. An empty log invites the first message,
 * "Cheer them on" (the bubble wiggling once on the brand's stacked disc; "No messages yet" first for screen readers),
 * or, when history couldn't load, says so in a Notice with a retry. Signed out it offers one Sign in to chat button;
 * timed out or removed, the input is disabled and says why. A host gets a menu on each message (Delete, Time out 10
 * minutes, Remove from chat, which asks first), and an Undo toast after a timeout or removal. The stage's invite, joined
 * and left cards sit in the log beside messages (`StageCard`: an avatar pair and one line), with no menu: they say what
 * happened on the stage, and a ban leaves them, as the server does. Message bodies and names render as text, never HTML.
 */
export function ChatPanel({
  chat,
  signedIn,
  isHost,
  hostUserIds,
  currentUserId = null,
  onSignIn,
  onComposerFocus,
  head,
  onInviteOnStage,
  seatedUserIds = NO_KEYS,
  invite = null,
  prompt,
  gifs = false,
  className,
}: ChatPanelProps) {
  const { messages, remove, ban, unban, retry } = chat
  const pulse = useStagePulse()
  const headingId = useId()
  const hosts = useMemo(() => new Set(hostUserIds), [hostUserIds])
  const seated = useMemo(() => new Set(seatedUserIds), [seatedUserIds])
  const [removal, setRemoval] = useState<{ open: boolean; message: ChatMessage | null; returnTo: HTMLElement | null; refusedAsHost: boolean }>(
    { open: false, message: null, returnTo: null, refusedAsHost: false },
  )

  // ── The log: follow new messages at the bottom, count them in the pill while scrolled up ──
  const logRef = useRef<HTMLDivElement>(null)
  const atBottomRef = useRef(true)
  /**
   * Row keys shown since the log was last empty, so a row that comes back (a failed delete) isn't new. Rows that left
   * stay in it until a full chat's worth (200) has left; then it starts again from the rows shown and the rows this
   * panel's moderation is hiding (`hidingRef`).
   */
  const shownRef = useRef<Set<string> | null>(null)
  /**
   * Row keys a delete, timeout or removal from this panel hid, with how many of those requests still run: a request
   * that fails puts its rows back, and they mustn't count as new.
   */
  const hidingRef = useRef(new Map<string, number>())
  const messagesRef = useRef(messages)
  /** Keys of rows that arrived while scrolled up and are still shown: the pill's count. */
  const [unseen, setUnseen] = useState(NO_KEYS)

  /**
   * Where this panel last put the log to follow the newest row, until that scroll's event arrives: a row can grow in
   * between (an invite card gaining Join stage), and that event must not read as the viewer scrolling up.
   */
  const followedToRef = useRef<number | null>(null)
  /** Puts the log at its end at once, remembering it as this panel's own scroll when it moved. */
  const jumpToEnd = useCallback((log: HTMLDivElement) => {
    const before = log.scrollTop
    log.scrollTop = log.scrollHeight
    if (log.scrollTop !== before) followedToRef.current = log.scrollTop
  }, [])

  const scrollToBottom = useCallback((behavior: 'auto' | 'smooth') => {
    const log = logRef.current
    if (!log) return
    if (behavior === 'smooth' && typeof log.scrollTo === 'function' && !prefersReducedMotion()) {
      log.scrollTo({ top: log.scrollHeight, behavior })
    } else {
      jumpToEnd(log)
    }
    atBottomRef.current = true
  }, [jumpToEnd])

  useLayoutEffect(() => {
    messagesRef.current = messages
    const shown = shownRef.current
    if (messages.length === 0) {
      // Another stage, or an emptied log: the next messages are a fresh start.
      shownRef.current = null
      atBottomRef.current = true
      setUnseen(NO_KEYS)
      return
    }
    if (!shown) {
      shownRef.current = new Set(messages.map(rowKey))
      scrollToBottom('auto')
      return
    }
    const added = messages.filter((message) => !shown.has(rowKey(message)))
    added.forEach((message) => shown.add(rowKey(message)))
    // Someone whose message just arrived has stopped typing it.
    if (added.length) pulse?.spoke(added.map((message) => message.user.id))
    if (shown.size > messages.length + CHAT_MAX_MESSAGES) shownRef.current = new Set([...messages.map(rowKey), ...hidingRef.current.keys()])
    // This viewer's rows, and an invite for them: followed down even when scrolled up, so the invite is seen.
    const own = (message: ChatMessage) =>
      message.pending ||
      message.failed ||
      (currentUserId !== null && (message.user.id === currentUserId || (isChatCard(message) && message.kind === 'invite' && message.subject.id === currentUserId)))
    if (added.length > 0 && (atBottomRef.current || added.some(own))) {
      scrollToBottom('auto')
      setUnseen(NO_KEYS)
      return
    }
    // Removed rows leave the count; new ones from others join it.
    const present = new Set(messages.map(rowKey))
    const fresh = added.filter((message) => !own(message)).map(rowKey)
    setUnseen((current) => {
      const still = current.filter((key) => present.has(key))
      return fresh.length === 0 && still.length === current.length ? current : [...still, ...fresh]
    })
  }, [messages, currentUserId, scrollToBottom, pulse])

  // A hidden log has no scroll room, so messages arriving while it is off screen (another phone tab, the lowered
  // landscape layer) leave it at the top. When it shows again or changes size, a viewer following the newest messages
  // stays on them, and so when a row grows (an invite card gaining Join stage once the server's word is in). (A move
  // between layouts keeps its place: the page's MovableSlot puts the scroll back.)
  const listRef = useRef<HTMLOListElement>(null)
  const hasRows = messages.length > 0
  useLayoutEffect(() => {
    const log = logRef.current
    if (!log || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(() => {
      if (atBottomRef.current) jumpToEnd(log)
    })
    observer.observe(log)
    if (listRef.current) observer.observe(listRef.current)
    return () => observer.disconnect()
  }, [hasRows, jumpToEnd])

  const onScroll = () => {
    const log = logRef.current
    if (!log) return
    const followed = followedToRef.current
    followedToRef.current = null
    const gap = log.scrollHeight - log.scrollTop - log.clientHeight
    // This panel's own scroll to the end: still following, and to the end again if a row grew meanwhile.
    if (followed !== null && Math.abs(log.scrollTop - followed) < 1) {
      atBottomRef.current = true
      if (gap > BOTTOM_SLACK_PX) jumpToEnd(log)
      return
    }
    atBottomRef.current = gap <= BOTTOM_SLACK_PX
    if (atBottomRef.current) setUnseen(NO_KEYS)
  }

  const showNewest = () => {
    scrollToBottom('smooth')
    setUnseen(NO_KEYS)
    // The pill goes away; keep keyboard focus in the log rather than losing it to the page.
    logRef.current?.focus({ preventScroll: true })
  }

  // ── Moderation ──
  /** Undoes a ban on the stage it was made on, even if the viewer has moved to another stage since. */
  const undo = useCallback((made: StageChatBan, who: string) => {
    unban(made.user_id, made.stage_id).catch(() => toast({ tone: 'error', title: 'Couldn’t undo', description: `${who} still can’t chat here.` }))
  }, [unban])

  /** Keeps the rows a moderation request hides in `hidingRef` while it runs. */
  const hiding = useCallback(<T,>(rows: ChatMessage[], request: Promise<T>) => {
    const counts = hidingRef.current
    const keys = rows.map(rowKey)
    keys.forEach((key) => counts.set(key, (counts.get(key) ?? 0) + 1))
    const done = () =>
      keys.forEach((key) => {
        const left = (counts.get(key) ?? 1) - 1
        if (left > 0) counts.set(key, left)
        else counts.delete(key)
      })
    request.then(done, done)
    return request
  }, [])

  const actions = useMemo<RowActions>(() => ({
    onDelete: (message) => {
      hiding([message], remove(message.id)).catch(() => toast({ tone: 'error', title: 'Couldn’t delete the message', description: 'Try again.' }))
    },
    onTimeout: (message) => {
      const who = plainName(message.user)
      hiding(rowsBy(messagesRef.current, message.user.id), ban(message.user.id, 'timeout')).then(
        (made) => toast({ title: `Timed out ${who} for 10 minutes`, action: { label: 'Undo', onClick: () => undo(made, who) } }),
        (error) => toast(refusedAsHost(error)
          ? { tone: 'error', title: HOSTS_CANT_BE_MODERATED }
          : { tone: 'error', title: `Couldn’t time out ${who}`, description: 'Try again.' }),
      )
    },
    onRemove: (message, returnTo) => setRemoval({ open: true, message, returnTo, refusedAsHost: false }),
    onRetry: (message) => void retry(message),
    onInvite: onInviteOnStage ? (message) => onInviteOnStage(message.user) : undefined,
    onLike: pulse
      ? (message) => {
          if (!signedIn) return onSignIn()
          pulse.toggleLike(message).catch(() => toast({ tone: 'error', title: 'Couldn’t like the message', description: 'Try again.' }))
        }
      : undefined,
    log: logRef,
  }), [remove, ban, retry, undo, hiding, onInviteOnStage, pulse, signedIn, onSignIn])

  const removing = removal.message
  const removingName = removing ? plainName(removing.user) : ''
  const confirmRemoval = async () => {
    if (!removing) return
    try {
      const made = await hiding(rowsBy(messagesRef.current, removing.user.id), ban(removing.user.id, 'remove'))
      toast({ title: `Removed ${removingName} from chat`, action: { label: 'Undo', onClick: () => undo(made, removingName) } })
    } catch (error) {
      setRemoval((current) => ({ ...current, refusedAsHost: refusedAsHost(error) }))
      throw error
    }
  }
  // Cancelled, focus goes back to the row's menu button; confirmed, that row is gone, so to the log.
  const removalFinalFocus = () => (removal.returnTo?.isConnected ? removal.returnTo : logRef.current ?? true)

  const moderationFor = (message: ChatMessage): Moderation => {
    if (!isHost || message.pending || message.failed) return 'none'
    return hosts.has(message.user.id) || message.user.id === currentUserId ? 'delete' : 'full'
  }

  return (
    <section className={cn('watch-chat', className)} aria-labelledby={headingId}>
      {/* The region's name and the h2 its empty state (h3) nests under, in the panel so it moves with the chat. */}
      <h2 id={headingId} className="sr-only">
        Chat
      </h2>
      {head}
      <div className="watch-chat__feed">
        {/* Focusable so the keyboard can scroll it; a log announces new messages politely. */}
        <div ref={logRef} className="watch-chat__log" role="log" aria-label="Messages" aria-busy={chat.loading || undefined} tabIndex={0} onScroll={onScroll}>
          {messages.length > 0 ? (
            <ol ref={listRef} className="watch-chat__list">
              {messages.map((message) =>
                isChatCard(message) ? (
                  // The stage's invite, joined and left cards: what happened, worded from the card, with no menu. The
                  // viewer's open invite carries Join stage and Not now (StageCard offers them only on their own).
                  invite && message.id === invite.cardId ? (
                    <CardRow
                      key={rowKey(message)}
                      card={message}
                      viewerId={currentUserId}
                      answered={invite.answered}
                      onJoin={invite.onJoin}
                      onDecline={invite.onDecline}
                      note={invite.note}
                    />
                  ) : (
                    <CardRow key={rowKey(message)} card={message} viewerId={currentUserId} answered />
                  )
                ) : (
                  <MessageRow
                    key={rowKey(message)}
                    message={message}
                    hostAuthor={hosts.has(message.user.id)}
                    moderation={moderationFor(message)}
                    invitable={Boolean(onInviteOnStage) && moderationFor(message) === 'full' && !seated.has(message.user.id)}
                    actions={actions}
                  />
                ),
              )}
            </ol>
          ) : chat.loading ? null : chat.historyError ? (
            // A load failure is a Notice, not an empty state. Quiet: the hook retries by itself; the button asks at
            // once. The log is the live region already, so the notice isn't a second one.
            <Notice
              tone="error"
              aria-live="off"
              className="watch-chat__notice"
              action={
                <Button variant="secondary" size="compact" onClick={chat.reloadHistory}>
                  Try again
                </Button>
              }
            >
              We couldn’t load messages.
            </Notice>
          ) : (
            // Signed out, the foot's Sign in to chat is the one next step, so the empty state offers none. An h3 under
            // the panel's h2; the wrapper centres it in the log. The invitation shows; the plain meaning leads it for
            // screen readers.
            <div className="watch-chat__empty">
              <WaitingState art={<StackedGlyph icon={<MessageCircle />} motion="wiggle" />} title="Cheer them on" plain="No messages yet" />
            </div>
          )}
        </div>
        {unseen.length > 0 && (
          <Button variant="secondary" size="compact" elevated className="watch-chat__new" onClick={showNewest}>
            <ArrowDown aria-hidden="true" />
            New messages{' '}
            <span className="watch-chat__new-count">{unseen.length}</span>
          </Button>
        )}
      </div>

      {prompt && <div className="watch-chat__prompt">{prompt}</div>}

      <TypingLine />
      {signedIn ? (
        <Composer chat={chat} block={chatBlock(chat.blocked)} onFocus={onComposerFocus} gifs={gifs} />
      ) : (
        <div className="watch-chat__compose">
          {/* The header's sign-in glyph: on a phone this sits over the yellow Chat tab, whose bubble would repeat. */}
          <Button className="watch-chat__sign-in" onClick={onSignIn}>
            <LogIn aria-hidden="true" />
            Sign in to chat
          </Button>
        </div>
      )}

      <ConfirmDialog
        open={removal.open}
        onOpenChange={(open) => setRemoval((current) => ({ ...current, open }))}
        title={`Remove ${removingName} from this chat?`}
        description="Their messages disappear and they can’t chat here. They can still watch."
        confirmLabel="Remove from chat"
        failureMessage={removal.refusedAsHost ? `${HOSTS_CANT_BE_MODERATED}.` : `Couldn’t remove ${removingName}. Try again.`}
        onConfirm={confirmRemoval}
        finalFocus={removalFinalFocus}
      />
    </section>
  )
}

function Composer({ chat, block, onFocus, gifs }: { chat: StageChat; block: ChatBlock; onFocus?: () => void; gifs: boolean }) {
  const inputRef = useRef<HTMLInputElement>(null)
  const blocked = block.kind !== 'none'
  const pulse = useStagePulse()

  const submit = (event: FormEvent) => {
    event.preventDefault()
    void chat.send()
    pulse?.sent()
    // The send button disables as the draft empties; keep typing where it was.
    inputRef.current?.focus()
  }

  const type = (text: string) => {
    chat.setDraft(text)
    if (text.trim()) pulse?.typing()
  }

  // An emoji goes in at the caret (replacing a selection), and the caret follows it.
  const insertEmoji = (emoji: string) => {
    const input = inputRef.current
    const draft = chat.draft
    const start = input?.selectionStart ?? draft.length
    const end = input?.selectionEnd ?? draft.length
    const text = (draft.slice(0, start) + emoji + draft.slice(end)).slice(0, CHAT_MESSAGE_MAX_LENGTH)
    type(text)
    const caret = Math.min(start + emoji.length, text.length)
    requestAnimationFrame(() => input?.setSelectionRange(caret, caret))
  }

  // A single line: Enter sends (the form's implicit submit), Shift+Enter does nothing.
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && event.shiftKey) event.preventDefault()
  }

  return (
    <form className="watch-chat__compose" onSubmit={submit}>
      <div className="watch-chat__tools">
        <EmojiPicker onPick={insertEmoji} disabled={blocked} />
        {gifs && <GifPicker onPick={(gif) => void chat.sendGif(gif)} disabled={blocked} />}
      </div>
      <TextField
        ref={inputRef}
        className="watch-chat__field"
        label="Chat message"
        hideLabel
        placeholder="Send a message"
        autoComplete="off"
        enterKeyHint="send"
        maxLength={CHAT_MESSAGE_MAX_LENGTH}
        value={chat.draft}
        onChange={(event) => type(event.target.value)}
        onKeyDown={onKeyDown}
        onFocus={onFocus}
        disabled={blocked}
        description={blocked ? <BlockedCopy block={block} /> : undefined}
      />
      {/* A press on Send leaves focus in the field (a phone's keyboard stays up, the page stays pinned to it). */}
      <IconButton
        type="submit"
        variant="primary"
        label="Send"
        disabled={blocked || !chat.draft.trim()}
        onMouseDown={(event) => {
          if (document.activeElement === inputRef.current) event.preventDefault()
        }}
      >
        <ArrowUp />
      </IconButton>
    </form>
  )
}

function BlockedCopy({ block }: { block: ChatBlock }) {
  if (block.kind === 'timeout') {
    const time = block.until.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    return (
      <span className="watch-chat__blocked">
        <Timer aria-hidden="true" />
        You can chat again at {time}
      </span>
    )
  }
  return (
    <span className="watch-chat__blocked">
      <Ban aria-hidden="true" />
      You were removed from this chat
    </span>
  )
}

interface MessageRowProps {
  message: ChatMessage
  hostAuthor: boolean
  moderation: Moderation
  /** A host who may invite, on a viewer's message whose author has no seat on the stage: Invite on stage leads the menu. */
  invitable: boolean
  actions: RowActions
}

/**
 * One message, as the prototype: the avatar, then one flowing line of the author's name, the Host badge and the body.
 * The name and the body are each isolated in a <bdi> (a first name can carry direction characters; the body takes its
 * own direction), so neither can reorder the other. The handle isn't shown: screen readers hear it after the name, and
 * a pointer finds it as the avatar's tooltip (the avatar is hidden from screen readers, so they hear it once), so two
 * people with one first name can be told apart. Touch has neither until the profile card. An author shown by their
 * handle has neither repeat. Memoised: typing in the composer re-renders the panel, not 200 rows.
 */
const MessageRow = memo(function MessageRow({ message, hostAuthor, moderation, invitable, actions }: MessageRowProps) {
  const { user } = message
  const name = authorName(user)
  const handle = visibleName(user.name) && user.handle ? user.handle : null
  const state = message.failed ? 'failed' : message.pending ? 'pending' : undefined
  const triggerRef = useRef<HTMLButtonElement>(null)
  const popupRef = useRef<HTMLDivElement>(null)
  /** Delete or Time out was chosen: this row is about to go, and its menu button with it. */
  const leavingRef = useRef(false)
  const [menuOpen, setMenuOpen] = useState(false)
  // Invite on stage runs with the menu open, its item saying so, and closes it once the server has answered.
  const [inviting, setInviting] = useState(false)
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])
  const invite = async () => {
    if (inviting || !actions.onInvite) return
    setInviting(true)
    try {
      await actions.onInvite(message)
    } finally {
      if (mountedRef.current) {
        setInviting(false)
        setMenuOpen(false)
      }
    }
  }

  const finalFocus = () => {
    if (leavingRef.current) return actions.log.current ?? true
    // As Base UI does by default: back to the menu button, unless the person moved focus somewhere else meanwhile.
    const active = document.activeElement
    return !active || active === document.body || active === triggerRef.current || Boolean(popupRef.current?.contains(active))
  }
  const leaveWith = (act: (message: ChatMessage) => void) => () => {
    leavingRef.current = true
    act(message)
  }

  return (
    <li className="watch-chat__message" data-state={state}>
      <Avatar
        name={initialsSource(user.name, user.handle)}
        src={profileAvatarUrl(user.avatar_url)}
        size="sm"
        tone="auto"
        toneKey={personToneKey(user)}
        title={handle ? `@${handle}` : undefined}
      />
      <div className="watch-chat__content">
        <p className="watch-chat__line">
          <bdi className="watch-chat__name">{name}</bdi>
          {handle && <span className="sr-only"> @{handle}</span>}
          {hostAuthor && (
            <>
              {' '}
              <Badge tone="accent" size="compact">
                Host
              </Badge>
            </>
          )}{' '}
          {message.body && <bdi className="watch-chat__text" dir="auto">{message.body}</bdi>}
          {state === undefined && (
            <>
              {' '}
              <ChatTime createdAt={message.created_at} />
            </>
          )}
        </p>
        {message.gif && <ChatGifImage gif={message.gif} />}
        {state === 'pending' && <span className="sr-only">Sending</span>}
        {state === 'failed' && (
          <span className="watch-chat__failed">
            <CircleAlert aria-hidden="true" />
            Not sent
          </span>
        )}
      </div>
      {state === undefined && actions.onLike && <LikeButton message={message} onToggle={actions.onLike} />}
      {state === 'failed' ? (
        <IconButton variant="ghost" size="compact" label="Retry" className="watch-chat__action" onClick={() => actions.onRetry(message)}>
          <RotateCw />
        </IconButton>
      ) : moderation !== 'none' ? (
        <Menu
          ref={popupRef}
          align="end"
          open={menuOpen}
          onOpenChange={(open) => {
            if (open) leavingRef.current = false
            setMenuOpen(open)
          }}
          finalFocus={finalFocus}
          trigger={
            <IconButton ref={triggerRef} variant="ghost" size="compact" label={`Moderate ${plainName(user)}’s message`} className="watch-chat__action watch-chat__menu">
              <Ellipsis />
            </IconButton>
          }
        >
          {invitable && (
            <>
              <MenuItem
                icon={inviting ? <LoaderCircle className="watch-chat__busy-glyph" /> : <UserPlus />}
                closeOnClick={false}
                disabled={inviting}
                onClick={() => void invite()}
              >
                {inviting ? 'Inviting…' : 'Invite on stage'}
              </MenuItem>
              <MenuSeparator />
            </>
          )}
          <MenuItem icon={<Trash2 />} onClick={leaveWith(actions.onDelete)}>Delete</MenuItem>
          {moderation === 'full' && (
            <>
              <MenuItem icon={<Timer />} onClick={leaveWith(actions.onTimeout)}>Time out 10 minutes</MenuItem>
              <MenuItem icon={<Ban />} onClick={() => actions.onRemove(message, triggerRef.current)}>Remove from chat</MenuItem>
            </>
          )}
        </Menu>
      ) : null}
    </li>
  )
})
